import { useMemo, useState } from 'react';
import { Stethoscope } from 'lucide-react';
import { useSandboxCreateProfessional } from '@/services/mutations/sandbox';
import {
	APPROACHES,
	FOCUS_TOPICS,
	GENDERS,
	HIGHLIGHT_TAGS,
	MAX_FOCUS_TOPICS,
} from '@/constants/professionalTags';
import {
	SandboxChipToggle,
	SandboxField,
	SandboxModal,
	SandboxNotice,
	sandboxInputClass,
	sandboxSelectClass,
} from './SandboxModal';

interface Props {
	scenarioId: string;
	catalog: Sandbox.Catalog;
	isOpen: boolean;
	onClose: () => void;
}

const VISIBILITY_OPTIONS: Array<{
	key: Professionals.Visibility;
	label: string;
}> = [
	{ key: 'DIRECT_LINK', label: 'Oculto da busca (só por link) — seguro' },
	{ key: 'PUBLIC_SEARCH', label: 'Visível na busca pública' },
];

/** Cadastro de profissional de teste, já autorizado e com agenda. */
const SandboxProfessionalModal = ({
	scenarioId,
	catalog,
	isOpen,
	onClose,
}: Props) => {
	const defaultService = useMemo(
		() =>
			catalog.services.find((s) => /psic/i.test(s.service_name)) ??
			catalog.services[0],
		[catalog.services],
	);

	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [serviceIds, setServiceIds] = useState<string[]>(
		defaultService ? [defaultService.id] : [],
	);
	const [gender, setGender] = useState<Professionals.Gender | ''>('');
	const [highlightTag, setHighlightTag] = useState<string>('');
	const [mainApproach, setMainApproach] = useState<string>('');
	const [focusTopics, setFocusTopics] = useState<string[]>([]);
	const [preset, setPreset] =
		useState<Sandbox.AvailabilityPreset>('BUSINESS_HOURS');
	const [visibility, setVisibility] =
		useState<Professionals.Visibility>('DIRECT_LINK');
	const [authorized, setAuthorized] = useState(true);

	const reset = () => {
		setFirstName('');
		setLastName('');
		setServiceIds(defaultService ? [defaultService.id] : []);
		setGender('');
		setHighlightTag('');
		setMainApproach('');
		setFocusTopics([]);
		setPreset('BUSINESS_HOURS');
		setVisibility('DIRECT_LINK');
		setAuthorized(true);
	};

	const { mutate, isPending } = useSandboxCreateProfessional(
		scenarioId,
		() => {
			reset();
			onClose();
		},
	);

	const toggleService = (id: string) =>
		setServiceIds((prev) =>
			prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
		);

	const submit = () => {
		mutate({
			first_name: firstName.trim() || undefined,
			last_name: lastName.trim() || undefined,
			service_ids: serviceIds.length > 0 ? serviceIds : undefined,
			gender: gender || undefined,
			highlight_tag: (highlightTag || undefined) as
				| Professionals.HighlightTag
				| undefined,
			main_approach: (mainApproach || undefined) as
				| Professionals.Approach
				| undefined,
			focus_topics: focusTopics as Professionals.FocusTopic[],
			availability_preset: preset,
			visibility,
			authorized,
		});
	};

	return (
		<SandboxModal
			isOpen={isOpen}
			onClose={onClose}
			title="Criar profissional"
			description="Sai autorizado, com serviço vinculado e grade de horários criada pelo serviço real de disponibilidade."
			icon={<Stethoscope className="h-4 w-4" />}
			confirmText="Criar profissional"
			onConfirm={submit}
			confirmDisabled={serviceIds.length === 0}
			isPending={isPending}
			size="wide"
		>
			<div className="space-y-5 py-2">
				{catalog.is_production ? (
					<SandboxNotice tone="warning">
						Ambiente de produção: mantenha o profissional oculto da
						busca para não aparecer para usuários reais.
					</SandboxNotice>
				) : null}

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<SandboxField label="Nome">
						<input
							className={sandboxInputClass}
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							placeholder="gerado"
						/>
					</SandboxField>
					<SandboxField label="Sobrenome">
						<input
							className={sandboxInputClass}
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							placeholder="gerado"
						/>
					</SandboxField>
				</div>

				<SandboxField
					label="Serviços"
					hint={
						catalog.services.length === 0
							? 'Nenhum serviço PROFESSION ativo. Cadastre em Serviços.'
							: undefined
					}
				>
					<SandboxChipToggle
						options={catalog.services.map((s) => ({
							key: s.id,
							label: s.service_name,
						}))}
						selected={serviceIds}
						onToggle={toggleService}
					/>
				</SandboxField>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<SandboxField label="Agenda">
						<select
							className={sandboxSelectClass}
							value={preset}
							onChange={(e) =>
								setPreset(
									e.target
										.value as Sandbox.AvailabilityPreset,
								)
							}
						>
							{catalog.availability_presets.map((p) => (
								<option key={p.key} value={p.key}>
									{p.label}
								</option>
							))}
						</select>
					</SandboxField>
					<SandboxField label="Visibilidade">
						<select
							className={sandboxSelectClass}
							value={visibility}
							onChange={(e) =>
								setVisibility(
									e.target.value as Professionals.Visibility,
								)
							}
						>
							{VISIBILITY_OPTIONS.map((v) => (
								<option key={v.key} value={v.key}>
									{v.label}
								</option>
							))}
						</select>
					</SandboxField>
				</div>

				<div className="rounded-2xl border border-stone-100 bg-stone-50/60 p-4">
					<p className="text-[11px] font-black uppercase tracking-wide text-stone-400">
						Perfil público (usado pelo match)
					</p>
					<div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
						<SandboxField label="Gênero">
							<select
								className={sandboxSelectClass}
								value={gender}
								onChange={(e) =>
									setGender(
										e.target.value as Professionals.Gender,
									)
								}
							>
								<option value="">não informar</option>
								{GENDERS.map((g) => (
									<option key={g.key} value={g.key}>
										{g.label}
									</option>
								))}
							</select>
						</SandboxField>
						<SandboxField label="Tag de destaque">
							<select
								className={sandboxSelectClass}
								value={highlightTag}
								onChange={(e) =>
									setHighlightTag(e.target.value)
								}
							>
								<option value="">nenhuma</option>
								{HIGHLIGHT_TAGS.map((t) => (
									<option key={t.key} value={t.key}>
										{t.label}
									</option>
								))}
							</select>
						</SandboxField>
						<SandboxField label="Abordagem principal">
							<select
								className={sandboxSelectClass}
								value={mainApproach}
								onChange={(e) =>
									setMainApproach(e.target.value)
								}
							>
								<option value="">nenhuma</option>
								{APPROACHES.map((a) => (
									<option key={a.key} value={a.key}>
										{a.label}
									</option>
								))}
							</select>
						</SandboxField>
					</div>
					<SandboxField
						label={`Temas (até ${MAX_FOCUS_TOPICS})`}
						className="mt-4"
					>
						<SandboxChipToggle
							options={FOCUS_TOPICS.map((t) => ({
								key: t.key,
								label: t.label,
							}))}
							selected={focusTopics}
							max={MAX_FOCUS_TOPICS}
							onToggle={(key) =>
								setFocusTopics((prev) =>
									prev.includes(key)
										? prev.filter((k) => k !== key)
										: [...prev, key],
								)
							}
						/>
					</SandboxField>
				</div>

				<label className="flex cursor-pointer items-center gap-3 text-sm text-stone-700">
					<input
						type="checkbox"
						checked={authorized}
						onChange={(e) => setAuthorized(e.target.checked)}
						className="h-4 w-4 accent-stone-900"
					/>
					Já autorizado pelo admin (desmarque para simular a fila de
					autorização)
				</label>
			</div>
		</SandboxModal>
	);
};

export { SandboxProfessionalModal };
